import {
  ContactResponse,
  MediaGenerationSettingsResponse,
  PresenterProfileResponse,
  Slide,
} from "pages/media/types/";
import { useDataProvider } from "@refinedev/core";
import { MediaTemplateResponse } from "types/";
import {
  createTemplateEditorStore,
  DEFAULT_TEMPLATE_STATE,
} from "../store/useTemplateEditorStore";
import { Chapter, TemplateState, TemplateType } from "../types";
import { getChapterHasTransition, isDefaultPublicFontValid } from "../helpers";
import { mapServerTemplateToTemplateState } from "../helpers/mapToTemplateState";
import { useBrandKit } from "hooks/useBrandKit/";
import { usePublicMediaAssets } from "hooks/usePublicMediaAssets/";
import { useQuery } from "@tanstack/react-query";
import { mapServerTemplateSlidesToTemplates } from "../helpers/mapTemplateSlidesToTemplateState";
import { useMediaGenerationSettings } from "./useMediaGenerationSettings";
import { DEFAULT_MEDIA_VIDEO_ASPECT_RATIO } from "pages/media/constants/";
import { SectionWithSlides } from "hooks/useStoryboardWithSlides/";

/**
 * @description useTemplates hook is used to fetch templates only once after media settings are loaded. After that, it will map template server data to template editor store data. We use storeRef to check if the templates have already been loaded not to map the data again.
 */
export const useTemplates = (
  mediaSettings: ReturnType<typeof useMediaGenerationSettings>,
  slides: Slide[],
  organizationId: string | undefined,
  isFlashProject: boolean,
  storeRef: ReturnType<typeof createTemplateEditorStore> | undefined,
  storyboardSections: SectionWithSlides[],
  hasTransition: boolean,
  templateData: {
    brandKitData: ReturnType<typeof useBrandKit> | undefined;
    initialPresenter: PresenterProfileResponse | undefined;
    projectPresenters: PresenterProfileResponse[] | undefined;
    presenterRatiosById: Map<string, number>;
    contact: ContactResponse | undefined;
    contacts: ContactResponse[] | undefined;
    presenterRatio: number | undefined;
    logoRatio: number | undefined;
    isPresenterLoading: boolean;
  }
) => {
  const {
    brandKitData,
    initialPresenter,
    projectPresenters,
    presenterRatiosById,
    contact,
    contacts = [],
    presenterRatio,
    logoRatio,
    isPresenterLoading,
  } = templateData;

  const {
    mediaGenerationSettings,
    mediaAspectRatio: aspectRatio = DEFAULT_MEDIA_VIDEO_ASPECT_RATIO,
    isMediaSettingsLoading,
  } = mediaSettings;

  const shouldNotMapData =
    storeRef ||
    organizationId === undefined ||
    (mediaGenerationSettings === undefined && isMediaSettingsLoading) ||
    brandKitData === undefined ||
    // isPresenterLoading also covers contacts loading (folded in by the
    // caller) — the store is created only once, so mapping before contacts
    // load would permanently bake in the wrong (project-level) contact per
    // chapter.
    isPresenterLoading ||
    storyboardSections.length === 0 ||
    (isFlashProject && slides.length === 0);

  const templateIdsAndTypes = extractTemplatesFromMediaSettings(
    mediaGenerationSettings
  );
  const templateIds = Object.keys(templateIdsAndTypes);

  const dataProvider = useDataProvider();
  const resource = `media/${organizationId}/templates`;
  const { data, isFetching } = useQuery({
    queryKey: ["templates", resource, templateIds],
    queryFn: async () => {
      const results = await Promise.all(
        templateIds.map((id) =>
          dataProvider().getOne<MediaTemplateResponse>({
            resource,
            id,
          })
        )
      );
      return results.map((result) => result.data);
    },
    enabled: !!organizationId && templateIds.length > 0,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const serverTemplates = new Map<string, MediaTemplateResponse["template"]>(
    data?.map((template) => [template.id, template.template]) ?? []
  );

  const { defaultPublicFont } = usePublicMediaAssets();

  if (isFetching || shouldNotMapData) return;

  const commonAssets = {
    defaultPublicFont: isDefaultPublicFontValid(defaultPublicFont)
      ? defaultPublicFont
      : undefined,
    brandKitData,
    initialPresenter,
    contact,
    presenterRatio,
    logoRatio,
  };

  // The presenter identity is NOT stored in the template items (only its
  // position/size/shape are). It lives per-chapter in
  // mediaGenerationSettings.chapters[].presenter_id, so resolve each chapter's
  // avatar from there instead of applying the single project-level presenter to
  // every template. Falls back to the project-level presenter when a chapter
  // has no override or its presenter profile can't be found.
  const presentersById = new Map(
    (projectPresenters ?? []).map((profile) => [profile.id, profile])
  );
  // Chapters only persist a presenter_id, never a contact — reverse the
  // contacts list to find which contact owns each chapter's presenter.
  const contactByPresenterId = new Map(
    contacts.map((c) => [c.presenter_id, c])
  );
  const templateIdToPresenterId = new Map<string, string>();
  const sectionIdToPresenterId = new Map<string, string>();
  mediaGenerationSettings?.chapters.forEach((chapter) => {
    if (!chapter.presenter_id) return;
    sectionIdToPresenterId.set(chapter.chapter_id, chapter.presenter_id);
    if (chapter.chapter_template_id) {
      templateIdToPresenterId.set(
        chapter.chapter_template_id,
        chapter.presenter_id
      );
    }
    if (chapter.transition_template_id) {
      templateIdToPresenterId.set(
        chapter.transition_template_id,
        chapter.presenter_id
      );
    }
  });
  // Override the project-level presenter (and its image ratio/contact) with
  // the chapter's own presenter and contact — no fallback to the project's
  // own contact, so an unowned presenter leaves contact unset.
  const overrideAssetsForPresenter = (presenterId: string | undefined) => {
    if (!presenterId) return commonAssets;
    const chapterPresenter = presentersById.get(presenterId);
    if (!chapterPresenter) return commonAssets;
    return {
      ...commonAssets,
      initialPresenter: chapterPresenter,
      contact: contactByPresenterId.get(presenterId),
      presenterRatio: presenterRatiosById.get(presenterId) ?? presenterRatio,
    };
  };
  const resolveChapterCommonAssets = (templateId: string) =>
    overrideAssetsForPresenter(templateIdToPresenterId.get(templateId));
  const resolveSlideCommonAssets = (sectionId: string | undefined) =>
    sectionId
      ? overrideAssetsForPresenter(sectionIdToPresenterId.get(sectionId))
      : commonAssets;

  const firstSection = storyboardSections[0];
  const firstChapterSettings =
    firstSection &&
    mediaGenerationSettings?.chapters.find(
      (ch) => ch.chapter_id === firstSection.id
    );

  // Select the first chapter as soon as a section exists — even for a freshly
  // created project that has no media settings yet (so `firstChapterSettings`
  // is undefined). Gating on the section (not the settings) keeps
  // `currentChapter.sectionId` populated, which is what lets `useTemplateSetup`
  // later match the chapter by id and assign its `chapter_template_id`. The
  // template id is read optionally and stays undefined until setup runs.
  const currentChapter: Chapter =
    firstSection && !isFlashProject
      ? {
          sectionId: firstSection.id,
          topic: firstSection.topic,
          templateId: firstChapterSettings?.chapter_template_id ?? undefined,
          type: TemplateType.CHAPTER,
        }
      : {
          sectionId: "",
          topic: "",
          type: TemplateType.CHAPTER,
        };

  const slideNameToTemplateIdMap =
    data?.reduce<Record<string, string>>((acc, template) => {
      template.template.slides?.forEach((slide) => {
        if (slide.name) {
          acc[slide.name] = template.id;
        }
      });
      return acc;
    }, {}) ?? {};

  const templates = (() => {
    if (!data) return new Map<string, TemplateState>();
    if (isFlashProject) {
      const slideTemplates = mapServerTemplateSlidesToTemplates(
        data,
        slides,
        aspectRatio,
        commonAssets,
        resolveSlideCommonAssets
      );

      const introTemplateId =
        mediaSettings.mediaGenerationSettings?.introduction_template_id;
      const closingTemplateId =
        mediaSettings.mediaGenerationSettings?.closing_template_id;

      const addSpecialTemplate = (
        templateId: string | undefined,
        type: TemplateType
      ) => {
        if (!templateId) return;
        const templateData = data.find((t) => t.id === templateId);
        if (templateData) {
          slideTemplates.set(
            templateId,
            mapServerTemplateToTemplateState(
              templateData.template,
              type,
              aspectRatio,
              commonAssets
            )
          );
        }
      };

      addSpecialTemplate(
        introTemplateId ?? undefined,
        TemplateType.INTRODUCTION
      );
      addSpecialTemplate(closingTemplateId ?? undefined, TemplateType.CLOSING);

      return slideTemplates;
    }
    return new Map(
      data
        .filter((template) => template.id && templateIdsAndTypes[template.id])
        .map((template) => [
          template.id,
          mapServerTemplateToTemplateState(
            template.template,
            templateIdsAndTypes[template.id]!,
            aspectRatio,
            resolveChapterCommonAssets(template.id)
          ),
        ])
    );
  })();

  const defaultChapter = isFlashProject
    ? DEFAULT_TEMPLATE_STATE
    : mapServerTemplateToTemplateState(
        undefined,
        TemplateType.CHAPTER,
        aspectRatio,
        commonAssets
      );

  const currentSlide: string | undefined = (() => {
    if (!isFlashProject || !firstSection) return undefined;

    const transitionTemplateId = firstChapterSettings?.transition_template_id;
    const firstChapterHasTransition = getChapterHasTransition(
      firstChapterSettings,
      hasTransition
    );

    if (firstChapterHasTransition && transitionTemplateId) {
      return firstSection.slides.find(
        (slide) =>
          slide.uuid &&
          slideNameToTemplateIdMap[slide.uuid] === transitionTemplateId
      )?.uuid;
    }

    // No transitions: select the first non-transition slide
    return firstSection.slides.find(
      (slide) =>
        slide.uuid &&
        (!transitionTemplateId ||
          slideNameToTemplateIdMap[slide.uuid] !== transitionTemplateId)
    )?.uuid;
  })();

  const templateState: TemplateState = (() => {
    if (isFlashProject && currentSlide) {
      return templates.get(currentSlide) ?? defaultChapter;
    }
    if (!isFlashProject && currentChapter.templateId) {
      return templates.get(currentChapter.templateId) ?? defaultChapter;
    }
    return defaultChapter;
  })();

  const defaultTransition = isFlashProject
    ? DEFAULT_TEMPLATE_STATE
    : mapServerTemplateToTemplateState(
        undefined,
        TemplateType.TRANSITION,
        aspectRatio,
        commonAssets
      );
  const defaultIntro = mapServerTemplateToTemplateState(
    undefined,
    TemplateType.INTRODUCTION,
    aspectRatio,
    commonAssets
  );
  const defaultClosing = mapServerTemplateToTemplateState(
    undefined,
    TemplateType.CLOSING,
    aspectRatio,
    commonAssets
  );

  return {
    currentChapter,
    currentSlide,
    aspectRatio,
    templateState,
    templates,
    slideNameToTemplateIdMap,
    defaultChapter,
    defaultTransition,
    defaultIntro,
    defaultClosing,
    serverTemplates,
  };
};

export const extractTemplatesFromMediaSettings = (
  mediaSettings?: MediaGenerationSettingsResponse
) => {
  const entries = [
    ...(mediaSettings?.chapters.map(
      (chapter) => [chapter.chapter_template_id, TemplateType.CHAPTER] as const
    ) ?? []),
    ...(mediaSettings?.chapters.map(
      (chapter) =>
        [chapter.transition_template_id, TemplateType.TRANSITION] as const
    ) ?? []),
    [mediaSettings?.introduction_template_id, TemplateType.INTRODUCTION],
    [mediaSettings?.chapter_template_id, TemplateType.CHAPTER],
    [mediaSettings?.transition_template_id, TemplateType.TRANSITION],
    [mediaSettings?.closing_template_id, TemplateType.CLOSING],
  ];

  return Object.fromEntries(
    entries.filter((entry): entry is [string, TemplateType] => !!entry[0])
  );
};
